import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  DEFAULT_CROP,
  FEEDBACK_OPTIONS,
  FEEDBACK_QUESTION,
  createAnalyticsSessionId,
  getActiveCampaignsForDiscovery,
  getGrindsForCampaign,
  hasFeedbackFromViewer,
  hasGrindFromViewer,
  rankCampaignsForDiscovery,
  recordCampaignCompletion,
  recordCampaignView,
  saveGrind,
  saveFeedback
} from '../services/campaignStore';
import {
  getDestinationCtaLabel,
  getDestinationEmbed,
  getFeedbackQuestion,
  getInterestLabels,
  getMediaPreview,
  normalizeInterestIds
} from '../services/discoveryConfig';
import {
  areCloudGrindsEnabled,
  createCloudGrind,
  getCloudGrindCount,
  hasCloudGrind
} from '../services/cloudGrindStore';
import { getControlledCloudGrindTestCampaign } from '../services/controlledCloudGrindTestCampaign';
import {
  createTrustedCampaignImpression,
  getAuthoritativeUserProfile,
  recordTrustedDiscoveryActivity,
  settleTrustedDiscovery,
  shouldUseTrustedDiscovery,
  skipTrustedCampaignImpression,
  startTrustedDiscoverySession,
  submitTrustedDiscoveryFeedback
} from '../services/trustedDiscoveryClient';

const REQUIRED_SECONDS = 15;
const TRUSTED_ACTIVITY_HEARTBEAT_MS = 5000;

function UnifiedDiscoveryFeed({ userProfile, onNavigateToProfile, onUserProfileRefresh }) {
  const analyticsSessionIdRef = useRef(createAnalyticsSessionId());
  const viewedCampaignsRef = useRef(new Set());
  const [posts, setPosts] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [notification, setNotification] = useState(null);
  const [secondsActive, setSecondsActive] = useState(0);
  const [feedbackAnswer, setFeedbackAnswer] = useState('');
  const [writtenFeedback, setWrittenFeedback] = useState('');
  const [alreadySubmitted, setAlreadySubmitted] = useState(false);
  const [skipInProgress, setSkipInProgress] = useState(false);
  const [pageIsActive, setPageIsActive] = useState(() => !document.hidden && document.hasFocus());
  const [trustedDiscovery, setTrustedDiscovery] = useState(createInitialTrustedDiscoveryState());

  const viewerId = userProfile?.uid || userProfile?.email || 'local-viewer';
  const viewerInterestIds = useMemo(() => normalizeInterestIds(userProfile?.interestIds), [userProfile?.interestIds]);
  const viewerInterestLabels = useMemo(() => getInterestLabels(viewerInterestIds), [viewerInterestIds]);
  const isComplete = currentIndex >= posts.length;
  const currentPost = posts[currentIndex];
  const currentPostUsesTrustedDiscovery = shouldUseTrustedDiscovery(currentPost);
  const timerComplete = secondsActive >= REQUIRED_SECONDS;
  const trustedActiveTimeComplete = trustedDiscovery.activeTimeRequirementMet || trustedDiscovery.proofCreated;
  const trustedFlowBusy = ['starting', 'submitting_feedback', 'settling'].includes(trustedDiscovery.status);
  const nextCampaignDisabled = skipInProgress || trustedDiscovery.status === 'starting' || trustedDiscovery.status === 'settling';
  const trustedCanCompleteDiscovery = currentPostUsesTrustedDiscovery
    && timerComplete
    && trustedActiveTimeComplete
    && Boolean(feedbackAnswer)
    && !alreadySubmitted
    && !trustedFlowBusy
    && trustedDiscovery.status !== 'settled'
    && trustedDiscovery.status !== 'already_completed';
  const localCanCompleteDiscovery = !currentPostUsesTrustedDiscovery
    && timerComplete
    && Boolean(feedbackAnswer)
    && !alreadySubmitted;
  const canCompleteDiscovery = currentPostUsesTrustedDiscovery
    ? trustedCanCompleteDiscovery
    : localCanCompleteDiscovery;
  const progressPercent = Math.min((secondsActive / REQUIRED_SECONDS) * 100, 100);

  useEffect(() => {
    const objectUrls = [];
    let cancelled = false;

    const loadPosts = async () => {
      setLoading(true);
      try {
        const controlledCloudGrindTestCampaign = await getControlledCloudGrindTestCampaign().catch(() => null);

        const localCampaigns = await getActiveCampaignsForDiscovery(viewerInterestIds);
        const normalizedLocalCampaigns = localCampaigns.map((campaign) => {
          const visualUrl = campaign.visual?.blob ? URL.createObjectURL(campaign.visual.blob) : '';
          if (visualUrl) objectUrls.push(visualUrl);
          return {
            ...campaign,
            visualUrl,
            crop: campaign.visual?.crop || DEFAULT_CROP
          };
        });

        const cloudTestCampaigns = controlledCloudGrindTestCampaign ? [controlledCloudGrindTestCampaign] : [];
        const rankedPosts = rankCampaignsForDiscovery([
          ...normalizedLocalCampaigns,
          ...cloudTestCampaigns
        ], viewerInterestIds);
        const visiblePosts = await filterCompletedCampaigns(rankedPosts, viewerId);
        const postsWithGrinds = await addGrindState(visiblePosts, viewerId);
        if (!cancelled) {
          setPosts(postsWithGrinds);
          setCurrentIndex(0);
        }
      } catch (error) {
        console.error('Error fetching discovery campaigns:', error);
        const localCampaigns = await getActiveCampaignsForDiscovery(viewerInterestIds).catch(() => []);
        const normalizedLocalCampaigns = localCampaigns.map((campaign) => {
          const visualUrl = campaign.visual?.blob ? URL.createObjectURL(campaign.visual.blob) : '';
          if (visualUrl) objectUrls.push(visualUrl);
          return {
            ...campaign,
            visualUrl,
            crop: campaign.visual?.crop || DEFAULT_CROP
          };
        });
        const visiblePosts = await filterCompletedCampaigns(normalizedLocalCampaigns, viewerId);
        const postsWithGrinds = await addGrindState(visiblePosts, viewerId);
        if (!cancelled) {
          setPosts(postsWithGrinds);
          setCurrentIndex(0);
        }
      }
      if (!cancelled) setLoading(false);
    };

    loadPosts();

    return () => {
      cancelled = true;
      objectUrls.forEach((url) => URL.revokeObjectURL(url));
    };
  }, [viewerId, viewerInterestIds]);

  useEffect(() => {
    setSecondsActive(0);
    setFeedbackAnswer('');
    setWrittenFeedback('');
    setAlreadySubmitted(false);
    setSkipInProgress(false);
    setPageIsActive(!document.hidden && document.hasFocus());
    setTrustedDiscovery(createInitialTrustedDiscoveryState());
  }, [currentPost?.id]);

  useEffect(() => {
    if (isComplete || !currentPost) return undefined;
    if (shouldUseTrustedDiscovery(currentPost)) {
      setAlreadySubmitted(false);
      return undefined;
    }

    let cancelled = false;
    hasFeedbackFromViewer(currentPost.id, viewerId).then((submitted) => {
      if (!cancelled) setAlreadySubmitted(submitted);
    });

    return () => {
      cancelled = true;
    };
  }, [currentIndex, currentPost, isComplete, viewerId]);

  useEffect(() => {
    if (isComplete || !currentPost || !currentPostUsesTrustedDiscovery) return undefined;

    let cancelled = false;
    setTrustedDiscovery((state) => ({
      ...state,
      status: 'starting',
      message: 'Preparing trusted Discovery...'
    }));

    async function startTrustedFlow() {
      try {
        const impression = await createTrustedCampaignImpression(currentPost.id);
        if (cancelled) return;

        const session = await startTrustedDiscoverySession(impression.impressionId);
        if (cancelled) return;

        setTrustedDiscovery((state) => ({
          ...state,
          status: 'active',
          message: 'Trusted Discovery is active.',
          impressionId: impression.impressionId,
          lockedTokenRate: impression.tokenRate || currentPost.completionTokenRate || 0,
          matchedAudienceId: impression.matchedAudienceId || '',
          activeTimeRequirementMet: session.activeTimeRequirementMet === true,
          feedbackRequirementMet: session.feedbackRequirementMet === true,
          proofCreated: session.proofCreated === true
        }));
      } catch (error) {
        if (cancelled) return;
        const alreadyCompleted = isAlreadyCompletedError(error);
        setAlreadySubmitted(alreadyCompleted);
        setTrustedDiscovery((state) => ({
          ...state,
          status: alreadyCompleted ? 'already_completed' : isOutOfRewardsError(error) ? 'insufficient_funding' : 'error',
          message: alreadyCompleted
            ? 'You already completed this Discovery.'
            : isOutOfRewardsError(error)
              ? 'This campaign is out of reward funding right now.'
            : 'Discovery is temporarily unavailable. Please try again shortly.',
          errorCode: error.code || ''
        }));
      }
    }

    startTrustedFlow();

    return () => {
      cancelled = true;
    };
  }, [currentIndex, currentPost, currentPostUsesTrustedDiscovery, isComplete]);

  useEffect(() => {
    if (isComplete || !currentPost || viewedCampaignsRef.current.has(currentPost.id)) return;

    viewedCampaignsRef.current.add(currentPost.id);
    recordCampaignView({
      campaignId: currentPost.id,
      creatorId: currentPost.creatorId || 'legacy-creator',
      campaignTitle: currentPost.title,
      viewerId,
      platform: currentPost.platform,
      sessionId: analyticsSessionIdRef.current
    }).catch((error) => {
      console.error('Error recording campaign view:', error);
    });
  }, [currentIndex, currentPost, isComplete, viewerId]);

  useEffect(() => {
    const updatePageActivity = () => {
      setPageIsActive(!document.hidden && document.hasFocus());
    };

    document.addEventListener('visibilitychange', updatePageActivity);
    window.addEventListener('focus', updatePageActivity);
    window.addEventListener('blur', updatePageActivity);

    return () => {
      document.removeEventListener('visibilitychange', updatePageActivity);
      window.removeEventListener('focus', updatePageActivity);
      window.removeEventListener('blur', updatePageActivity);
    };
  }, []);

  useEffect(() => {
    if (isComplete || !pageIsActive || timerComplete) return undefined;

    const timer = setInterval(() => {
      setSecondsActive((seconds) => Math.min(seconds + 1, REQUIRED_SECONDS));
    }, 1000);

    return () => clearInterval(timer);
  }, [isComplete, pageIsActive, timerComplete]);

  useEffect(() => {
    if (
      isComplete
      || !currentPostUsesTrustedDiscovery
      || !pageIsActive
      || !trustedDiscovery.impressionId
      || trustedDiscovery.activeTimeRequirementMet
      || trustedDiscovery.proofCreated
      || trustedDiscovery.status === 'settled'
      || trustedDiscovery.status === 'already_completed'
      || trustedDiscovery.status === 'error'
    ) {
      return undefined;
    }

    let activityInFlight = false;
    const activityTimer = setInterval(async () => {
      if (activityInFlight) return;
      activityInFlight = true;
      try {
        const session = await recordTrustedDiscoveryActivity(trustedDiscovery.impressionId);
        setTrustedDiscovery((state) => ({
          ...state,
          status: state.status === 'starting' ? 'active' : state.status,
          activeTimeRequirementMet: session.activeTimeRequirementMet === true,
          feedbackRequirementMet: session.feedbackRequirementMet === true,
          proofCreated: session.proofCreated === true,
          qualifyingActiveMs: session.qualifyingActiveMs || state.qualifyingActiveMs
        }));
      } catch (error) {
        setTrustedDiscovery((state) => ({
          ...state,
          status: 'error',
          message: 'Discovery activity could not be confirmed. Please try again shortly.',
          errorCode: error.code || ''
        }));
      } finally {
        activityInFlight = false;
      }
    }, TRUSTED_ACTIVITY_HEARTBEAT_MS);

    return () => clearInterval(activityTimer);
  }, [
    currentPostUsesTrustedDiscovery,
    isComplete,
    pageIsActive,
    trustedDiscovery.activeTimeRequirementMet,
    trustedDiscovery.impressionId,
    trustedDiscovery.proofCreated,
    trustedDiscovery.status
  ]);

  const showNotification = (message, type = 'info') => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 5000);
  };

  const handleDestinationVisit = () => {
    const destination = getPrimaryDestination(currentPost);
    const url = formatUrl(destination?.url);
    if (!url) {
      showNotification('This campaign does not have a primary destination link yet.', 'info');
      return;
    }

    showNotification('Destination opened. External visits and engagement do not earn PromoGrind tokens.', 'info');
    const openedWindow = window.open(url, '_blank', 'noopener,noreferrer');
    if (openedWindow) openedWindow.opener = null;
  };

  const handleLearnMoreVisit = () => {
    const url = formatUrl(currentPost?.learnMoreLink);
    if (!url) {
      showNotification('This campaign does not have a Learn More link.', 'info');
      return;
    }

    showNotification('Legacy Learn More opened. External visits do not earn PromoGrind tokens.', 'info');
    const openedWindow = window.open(url, '_blank', 'noopener,noreferrer');
    if (openedWindow) openedWindow.opener = null;
  };

  const handleCompleteDiscovery = async () => {
    if (!canCompleteDiscovery || !currentPost) return;

    if (currentPostUsesTrustedDiscovery) {
      await handleTrustedDiscoveryCompletion();
      return;
    }

    try {
      const savedFeedback = await saveFeedback({
        campaignId: currentPost.id,
        creatorId: currentPost.creatorId || 'legacy-creator',
        campaignTitle: currentPost.title,
        viewerId,
        answer: feedbackAnswer,
        message: writtenFeedback.trim(),
        feedbackQuestion: getFeedbackQuestion(currentPost.feedbackQuestion),
        platform: currentPost.platform
      });

      recordCampaignCompletion({
        campaignId: currentPost.id,
        creatorId: currentPost.creatorId || 'legacy-creator',
        campaignTitle: currentPost.title,
        viewerId,
        platform: currentPost.platform,
        feedbackId: savedFeedback.id
      }).catch((error) => {
        console.error('Error recording campaign completion:', error);
      });

      showNotification(`Feedback saved locally. Future completed Discovery rate: ${currentPost.completionTokenRate || 0} tokens.`, 'success');
      setPosts((campaigns) => campaigns.filter((campaign) => campaign.id !== currentPost.id));
      setCurrentIndex((index) => Math.min(index, Math.max(posts.length - 2, 0)));
      setSecondsActive(0);
      setFeedbackAnswer('');
      setWrittenFeedback('');
      setAlreadySubmitted(false);
    } catch (error) {
      if (error?.name === 'ConstraintError') {
        setAlreadySubmitted(true);
        showNotification('You already submitted feedback for this campaign in this browser.', 'info');
        return;
      }
      showNotification(`Feedback could not be saved locally: ${error.message}`, 'info');
    }
  };

  const handleTrustedDiscoveryCompletion = async () => {
    if (!trustedDiscovery.impressionId) {
      showNotification('Discovery is still preparing. Please try again in a moment.', 'info');
      return;
    }

    try {
      setTrustedDiscovery((state) => ({
        ...state,
        status: 'submitting_feedback',
        message: 'Saving feedback...'
      }));
      const session = await submitTrustedDiscoveryFeedback({
        impressionId: trustedDiscovery.impressionId,
        answer: feedbackAnswer,
        message: writtenFeedback.trim()
      });

      if (session.proofCreated !== true) {
        setTrustedDiscovery((state) => ({
          ...state,
          status: 'active',
          activeTimeRequirementMet: session.activeTimeRequirementMet === true,
          feedbackRequirementMet: session.feedbackRequirementMet === true,
          proofCreated: false,
          message: 'Feedback saved. Keep Discovery active while completion is confirmed.'
        }));
        showNotification('Feedback saved. Completion is still being confirmed.', 'info');
        return;
      }

      setTrustedDiscovery((state) => ({
        ...state,
        status: 'settling',
        activeTimeRequirementMet: true,
        feedbackRequirementMet: true,
        proofCreated: true,
        message: 'Confirming reward...'
      }));
      const settlement = await settleTrustedDiscovery(trustedDiscovery.impressionId);

      if (settlement.settled === true) {
        const refreshedProfile = await getAuthoritativeUserProfile(viewerId).catch(() => null);
        if (refreshedProfile && onUserProfileRefresh) {
          onUserProfileRefresh(refreshedProfile);
        }
        setTrustedDiscovery((state) => ({
          ...state,
          status: 'settled',
          settlement,
          rewardAmount: settlement.tokenRate || state.lockedTokenRate,
          message: `Reward confirmed: ${settlement.tokenRate || state.lockedTokenRate} tokens.`
        }));
        showNotification(`Reward confirmed: ${settlement.tokenRate || trustedDiscovery.lockedTokenRate} tokens earned.`, 'success');
        setAlreadySubmitted(true);
        return;
      }

      if (settlement.idempotent) {
        setAlreadySubmitted(true);
        setTrustedDiscovery((state) => ({
          ...state,
          status: 'already_completed',
          settlement,
          message: 'You already completed this Discovery.'
        }));
        showNotification('You already completed this Discovery.', 'info');
        return;
      }

      setTrustedDiscovery((state) => ({
        ...state,
        status: settlement.reason === 'insufficient_funding' ? 'insufficient_funding' : 'error',
        settlement,
        message: settlement.reason === 'insufficient_funding'
          ? 'This campaign is out of reward funding right now.'
          : 'Reward could not be confirmed. Please try again shortly.'
      }));
      showNotification(
        settlement.reason === 'insufficient_funding'
          ? 'This campaign is out of reward funding right now.'
          : 'Reward could not be confirmed. Please try again shortly.',
        'info'
      );
    } catch (error) {
      const alreadyCompleted = isAlreadyCompletedError(error);
      setAlreadySubmitted(alreadyCompleted);
      setTrustedDiscovery((state) => ({
        ...state,
        status: alreadyCompleted ? 'already_completed' : 'error',
        message: alreadyCompleted
          ? 'You already completed this Discovery.'
          : 'Reward could not be confirmed. Please try again shortly.',
        errorCode: error.code || ''
      }));
      showNotification(
        alreadyCompleted ? 'You already completed this Discovery.' : 'Reward could not be confirmed. Please try again shortly.',
        'info'
      );
    }
  };

  const handleGrind = async () => {
    if (!currentPost || currentPost.viewerHasGrinded) return;

    try {
      await saveGrindForCampaign(currentPost, viewerId);

      setPosts((campaigns) => campaigns.map((campaign) => (
        campaign.id === currentPost.id
          ? {
              ...campaign,
              viewerHasGrinded: true,
              grindCount: (campaign.grindCount || 0) + 1
            }
          : campaign
      )));
      showNotification('Grind added. Grinds are free and do not complete Discovery.', 'success');
    } catch (error) {
      if (error?.name === 'ConstraintError') {
        setPosts((campaigns) => campaigns.map((campaign) => (
          campaign.id === currentPost.id
            ? { ...campaign, viewerHasGrinded: true }
            : campaign
        )));
        showNotification('You already Grinded this campaign in this browser.', 'info');
        return;
      }
      showNotification(`Grind could not be saved locally: ${error.message}`, 'info');
    }
  };

  const handleNextCampaign = async () => {
    if (!currentPost || skipInProgress || nextCampaignDisabled) return;

    setSkipInProgress(true);
    try {
      if (
        currentPostUsesTrustedDiscovery
        && trustedDiscovery.impressionId
        && trustedDiscovery.status !== 'settled'
        && trustedDiscovery.status !== 'already_completed'
      ) {
        await skipTrustedCampaignImpression(trustedDiscovery.impressionId);
      }

      advancePastCurrentCampaign();
    } catch (error) {
      showNotification(`Campaign could not be skipped: ${error.message}`, 'info');
    } finally {
      setSkipInProgress(false);
    }
  };

  const advancePastCurrentCampaign = () => {
    if (!currentPost) return;
    setPosts((campaigns) => campaigns.filter((campaign) => campaign.id !== currentPost.id));
    setCurrentIndex((index) => Math.min(index, Math.max(posts.length - 2, 0)));
  };

  if (loading) {
    return <div style={loadingStyle}>Loading Discovery...</div>;
  }

  if (posts.length === 0) {
    return (
      <div style={emptyStyle}>
        <h2>You're all caught up. Check back soon for more campaigns.</h2>
        <button type="button" onClick={onNavigateToProfile} style={secondaryButtonStyle}>
          Edit Interests
        </button>
      </div>
    );
  }

  const destination = getPrimaryDestination(currentPost);
  const feedbackQuestion = getFeedbackQuestion(currentPost.feedbackQuestion) || FEEDBACK_QUESTION;
  const mediaPreview = getTrustedMediaPreview(currentPost);
  const targetInterestLabels = getInterestLabels(currentPost.targetInterestIds);
  const hasLearnMore = Boolean(currentPost.learnMoreLink);

  return (
    <div style={pageStyle}>
      {notification && (
        <div role="status" aria-live="polite" style={noticeStyle(notification.type)}>
          {notification.message}
        </div>
      )}

      <header style={heroStyle}>
        <h1 style={heroTitleStyle}>Discovery</h1>
        <p style={heroCopyStyle}>
          Prioritizing {viewerInterestLabels.join(', ')}. Other eligible campaigns may appear after matched inventory.
        </p>
        <div style={heroMetaStyle}>
          Campaign {currentIndex + 1} of {posts.length}
        </div>
      </header>

      <article style={cardStyle}>
        <div style={previewModeStyle}>
          {currentPostUsesTrustedDiscovery ? 'Controlled reward test' : 'Preview Mode - rewards are not active yet'}
        </div>
        <h2 style={campaignTitleStyle}>{currentPost.title}</h2>

        <div style={audienceStyle(currentPost.matchesViewerInterests)}>
          {targetInterestLabels.length > 0 ? (
            <>
              <strong>Audience:</strong> {targetInterestLabels.join(', ')}
              <span>{currentPost.matchesViewerInterests ? 'Matched to your interests' : 'Fallback discovery'}</span>
            </>
          ) : (
            <>
              <strong>Legacy campaign</strong>
              <span>Included as fallback eligible inventory.</span>
            </>
          )}
        </div>

        {currentPost.visualUrl ? (
          <div style={visualFrameStyle}>
            <CroppedCampaignImage imageUrl={currentPost.visualUrl} crop={currentPost.crop || DEFAULT_CROP} />
          </div>
        ) : (
          <div style={placeholderVisualStyle}>
            <strong>PromoGrind</strong>
            <span>Campaign preview</span>
          </div>
        )}

        {(currentPost.creatorType || currentPost.creatorName || currentPost.message) && (
          <div style={campaignDetailsStyle}>
            {currentPost.creatorType && <div style={creatorTypePillStyle}>{currentPost.creatorType}</div>}
            {currentPost.creatorName && <strong>{currentPost.creatorName}</strong>}
            {currentPost.message && <p style={messageStyle}>{currentPost.message}</p>}
          </div>
        )}

        {destination && (
          <DestinationPreview
            destination={destination}
            visualUrl={getPreviewImageUrl(currentPost)}
            title={currentPost.title}
            onVisit={handleDestinationVisit}
          />
        )}

        <div style={grindRowStyle}>
          <button
            type="button"
            onClick={handleGrind}
            disabled={currentPost.viewerHasGrinded}
            style={grindButtonStyle(currentPost.viewerHasGrinded)}
          >
            {formatGrindButton(currentPost.grindCount || 0, currentPost.viewerHasGrinded)}
          </button>
          <span style={grindHelpStyle}>Free encouragement. No tokens move.</span>
        </div>

        {mediaPreview.status !== 'empty' && (
          <div style={mediaSectionStyle}>
            <h3 style={sectionHeadingStyle}>Optional Media</h3>
            <TrustedMediaPreview mediaPreview={mediaPreview} />
          </div>
        )}

        <div style={stepGridStyle}>
          <div style={stepCardStyle(true)}>
            <strong>1. Examine card</strong>
            <span>Required for completion</span>
          </div>
          <div style={stepCardStyle(timerComplete)}>
            <strong>2. Stay active for 15 seconds</strong>
            <span>Required for completion</span>
          </div>
          <div style={stepCardStyle(Boolean(feedbackAnswer))}>
            <strong>3. Answer feedback question</strong>
            <span>Required for completion</span>
          </div>
        </div>

        <div style={timerStyle}>
          <div style={timerHeaderStyle}>
            <strong>Active discovery time</strong>
            <span>{Math.min(secondsActive, REQUIRED_SECONDS)} / {REQUIRED_SECONDS} seconds</span>
          </div>
          <div role="progressbar" aria-valuemin="0" aria-valuemax={REQUIRED_SECONDS} aria-valuenow={Math.min(secondsActive, REQUIRED_SECONDS)} aria-label="Active discovery timer" style={progressTrackStyle}>
            <div style={{ ...progressFillStyle, width: `${progressPercent}%`, backgroundColor: timerComplete ? '#28a745' : '#007bff' }} />
          </div>
          <small style={helperStyle}>Timer pauses when this browser tab is hidden or inactive.</small>
          {currentPostUsesTrustedDiscovery && !trustedActiveTimeComplete && timerComplete && (
            <small style={helperStyle}>Still confirming active Discovery time.</small>
          )}
        </div>

        <fieldset style={feedbackFieldsetStyle}>
          <legend style={feedbackLegendStyle}>{feedbackQuestion}</legend>
          <div style={feedbackOptionsStyle}>
            {FEEDBACK_OPTIONS.map((option) => (
              <label key={option} style={feedbackOptionStyle(feedbackAnswer === option)}>
                <input
                  type="radio"
                  name={`feedback-${currentPost.id}`}
                  value={option}
                  checked={feedbackAnswer === option}
                  disabled={alreadySubmitted}
                  onChange={(event) => setFeedbackAnswer(event.target.value)}
                />
                {option}
              </label>
            ))}
          </div>
        </fieldset>

        <label htmlFor={`written-feedback-${currentPost.id}`} style={labelStyle}>
          Optional written feedback
        </label>
        <textarea
          id={`written-feedback-${currentPost.id}`}
          value={writtenFeedback}
          onChange={(event) => setWrittenFeedback(event.target.value.slice(0, 200))}
          disabled={alreadySubmitted}
          maxLength={200}
          rows={3}
          placeholder="Add a short note for the creator, if helpful."
          style={textareaStyle}
        />
        <small style={helperStyle}>{writtenFeedback.length}/200 characters. No additional reward.</small>

        {alreadySubmitted && <div style={alreadySubmittedStyle}>You already submitted feedback for this campaign in this browser.</div>}

        {currentPostUsesTrustedDiscovery && trustedDiscovery.message && (
          <div role="status" aria-live="polite" style={trustedStatusStyle(trustedDiscovery.status)}>
            {trustedDiscovery.message}
          </div>
        )}

        {hasLearnMore && (
          <div style={legacyLearnMoreStyle}>
            <button type="button" onClick={handleLearnMoreVisit} style={learnMoreButtonStyle}>
              Learn More
            </button>
            <small style={helperStyle}>Legacy context link. It is separate from the primary destination.</small>
          </div>
        )}

        <div style={rewardNoteStyle}>
          {currentPostUsesTrustedDiscovery
            ? `Reward: ${trustedDiscovery.lockedTokenRate || currentPost.completionTokenRate || 0} tokens after trusted confirmation.`
            : `Future completed Discovery rate: ${currentPost.completionTokenRate || 0} tokens. No tokens move in this local stage.`}
        </div>

        <button type="button" onClick={handleCompleteDiscovery} disabled={!canCompleteDiscovery} style={completeButtonStyle(canCompleteDiscovery)}>
          {currentPostUsesTrustedDiscovery ? getTrustedCompleteButtonLabel(trustedDiscovery) : 'Complete Discovery'}
        </button>
        <button type="button" onClick={handleNextCampaign} disabled={nextCampaignDisabled} style={nextButtonStyle(nextCampaignDisabled)}>
          {skipInProgress ? 'Moving to Next Campaign' : 'Next Campaign'}
        </button>
      </article>
    </div>
  );
}

function createInitialTrustedDiscoveryState() {
  return {
    status: 'idle',
    message: '',
    impressionId: '',
    lockedTokenRate: 0,
    matchedAudienceId: '',
    qualifyingActiveMs: 0,
    activeTimeRequirementMet: false,
    feedbackRequirementMet: false,
    proofCreated: false,
    rewardAmount: 0,
    settlement: null,
    errorCode: ''
  };
}

function isAlreadyCompletedError(error) {
  return error?.code === 'already-exists' || /already/i.test(error?.message || '');
}

function isOutOfRewardsError(error) {
  return /run out of rewards|out of rewards|insufficient_funding|cannot currently fund/i.test(error?.message || '');
}

function getTrustedCompleteButtonLabel(trustedDiscovery) {
  if (trustedDiscovery.status === 'starting') return 'Preparing Discovery';
  if (trustedDiscovery.status === 'submitting_feedback') return 'Saving Feedback';
  if (trustedDiscovery.status === 'settling') return 'Confirming Reward';
  if (trustedDiscovery.status === 'settled') return 'Reward Confirmed';
  if (trustedDiscovery.status === 'already_completed') return 'Already Completed';
  if (trustedDiscovery.status === 'insufficient_funding') return 'Reward Funding Unavailable';
  return 'Complete Discovery';
}

async function filterCompletedCampaigns(campaigns, viewerId) {
  const completionChecks = await Promise.all(
    campaigns.map(async (campaign) => ({
      campaign,
      completed: await hasFeedbackFromViewer(campaign.id, viewerId).catch(() => false)
    }))
  );

  return completionChecks
    .filter(({ completed }) => !completed)
    .map(({ campaign }) => campaign);
}

async function addGrindState(campaigns, viewerId) {
  return Promise.all(campaigns.map(async (campaign) => {
    const [grindCount, viewerHasGrinded] = await Promise.all([
      getGrindCountForCampaign(campaign, viewerId),
      hasGrindForCampaign(campaign, viewerId)
    ]);

    return {
      ...campaign,
      grindCount: grindCount || campaign.grindCount || 0,
      viewerHasGrinded
    };
  }));
}

function shouldUseCloudGrinds(campaign) {
  return areCloudGrindsEnabled() && campaign?.source === 'cloud';
}

async function getGrindCountForCampaign(campaign, viewerId) {
  if (shouldUseCloudGrinds(campaign)) {
    return getCloudGrindCount(campaign.id).catch(() => 0);
  }
  const grinds = await getGrindsForCampaign(campaign.id).catch(() => []);
  return grinds.length;
}

async function hasGrindForCampaign(campaign, viewerId) {
  if (shouldUseCloudGrinds(campaign)) {
    return hasCloudGrind(campaign.id).catch(() => false);
  }
  return hasGrindFromViewer(campaign.id, viewerId).catch(() => false);
}

async function saveGrindForCampaign(campaign, viewerId) {
  if (shouldUseCloudGrinds(campaign)) {
    return createCloudGrind(campaign);
  }

  return saveGrind({
    campaignId: campaign.id,
    creatorId: campaign.creatorId || 'legacy-creator',
    campaignTitle: campaign.title,
    viewerId,
    platform: campaign.platform
  });
}

function formatGrindButton(count, viewerHasGrinded) {
  if (count > 0) return `🔥 ${count} ${count === 1 ? 'Grind' : 'Grinds'}`;
  return viewerHasGrinded ? '🔥 1 Grind' : '🔥 Grind';
}

function formatUrl(url) {
  if (!url) return '';
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  return `https://${url}`;
}

function getPrimaryDestination(post) {
  if (post?.destination?.url) return post.destination;

  if (post?.source === 'firestore' && post.link) {
    return {
      platformId: 'legacy',
      label: 'Creator Link',
      url: post.link,
      trusted: false,
      kind: 'legacy'
    };
  }

  return null;
}

function getPreviewImageUrl(post) {
  return post?.visualUrl || post?.imageUrl || post?.thumbnailUrl || post?.creatorImageUrl || post?.profileImageUrl || post?.avatarUrl || '';
}

function getTrustedMediaPreview(post) {
  if (post?.mediaPreview?.status === 'valid') return post.mediaPreview;
  if (post?.mediaLink) return getMediaPreview(post.mediaLink);
  return { status: 'empty' };
}

function CroppedCampaignImage({ imageUrl, crop }) {
  return (
    <img
      src={imageUrl}
      alt=""
      draggable="false"
      style={{
        display: 'block',
        width: '100%',
        height: '100%',
        objectFit: 'cover',
        objectPosition: `${crop.x}% ${crop.y}%`,
        transform: `scale(${crop.zoom})`,
        transformOrigin: `${crop.x}% ${crop.y}%`
      }}
    />
  );
}

function DestinationPreview({ destination, visualUrl, title, onVisit }) {
  const embedUrl = getDestinationEmbed(destination);
  const ctaLabel = getDestinationCtaLabel(destination);
  const hasArtwork = Boolean(visualUrl);

  return (
    <section style={destinationPreviewStyle} aria-label={`${destination.label} destination preview`}>
      <div style={destinationHeaderStyle}>
        <span style={destinationProviderPillStyle}>{destination.label}</span>
        <strong>{ctaLabel}</strong>
      </div>

      {embedUrl ? (
        <iframe title={`${destination.label} destination preview`} src={embedUrl} loading="lazy" allow="encrypted-media; fullscreen; picture-in-picture" sandbox="allow-scripts allow-same-origin allow-popups" style={destinationEmbedStyle} />
      ) : !hasArtwork ? (
        <div style={destinationFallbackVisualStyle}>
          <div style={destinationBrandFallbackStyle}>
            <strong>PromoGrind</strong>
            <span>Discovery destination</span>
          </div>
          <div style={destinationOverlayStyle}>
            <span>{destination.label}</span>
            <strong>{title || 'Campaign destination'}</strong>
          </div>
        </div>
      ) : (
        <p style={compactDestinationCopyStyle}>Continue from the campaign artwork to this external destination.</p>
      )}

      <button type="button" onClick={onVisit} style={destinationButtonStyle}>
        {ctaLabel}
      </button>
      <small style={helperStyle}>Primary external destination. Visiting or engaging outside PromoGrind does not complete discovery steps or earn tokens.</small>
    </section>
  );
}

function TrustedMediaPreview({ mediaPreview }) {
  if (mediaPreview.embedUrl) {
    return <iframe title={`${mediaPreview.provider} media preview`} src={mediaPreview.embedUrl} loading="lazy" allow="encrypted-media; fullscreen; picture-in-picture" sandbox="allow-scripts allow-same-origin allow-popups" style={mediaEmbedStyle} />;
  }

  if (mediaPreview.href) {
    return (
      <a href={mediaPreview.href} target="_blank" rel="noopener noreferrer" style={trustedMediaLinkStyle}>
        Open trusted {mediaPreview.provider} link
      </a>
    );
  }

  return <p style={{ color: '#666' }}>{mediaPreview.message}</p>;
}

const pageStyle = { padding: '30px 16px', maxWidth: 900, margin: '0 auto' };
const loadingStyle = { padding: 50, textAlign: 'center', fontSize: 18, color: '#666' };
const emptyStyle = { ...loadingStyle, backgroundColor: '#fff' };
const heroStyle = { background: 'linear-gradient(45deg, #0f766e, #2563eb)', borderRadius: 10, color: '#fff', marginBottom: 25, padding: 25, textAlign: 'center' };
const heroTitleStyle = { fontSize: 30, margin: '0 0 8px' };
const heroCopyStyle = { fontSize: 16, lineHeight: 1.45, margin: '0 auto 12px', maxWidth: 680 };
const heroMetaStyle = { backgroundColor: 'rgba(255,255,255,0.2)', borderRadius: 999, display: 'inline-block', fontWeight: 'bold', padding: '7px 12px' };
const cardStyle = { backgroundColor: '#fff', border: '3px solid #0f766e', borderRadius: 10, boxShadow: '0 4px 20px rgba(0,0,0,0.1)', marginBottom: 25, padding: 'clamp(20px, 4vw, 32px)' };
const previewModeStyle = { backgroundColor: '#eef7ff', border: '1px solid #b6dcff', borderRadius: 999, color: '#204766', display: 'inline-block', fontSize: 13, fontWeight: 'bold', marginBottom: 16, padding: '6px 12px' };
const campaignTitleStyle = { color: '#333', fontSize: 24, margin: '0 0 15px' };
const audienceStyle = (matched) => ({ backgroundColor: matched ? '#effaf1' : '#fff8e1', border: `1px solid ${matched ? '#28a745' : '#ffe08a'}`, borderRadius: 8, color: matched ? '#155724' : '#6b5300', display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 16, padding: 12 });
const visualFrameStyle = { aspectRatio: '16 / 9', backgroundColor: '#111', borderRadius: 10, marginBottom: 20, overflow: 'hidden', width: '100%' };
const placeholderVisualStyle = { alignItems: 'center', aspectRatio: '16 / 9', background: 'linear-gradient(135deg, #10233f 0%, #17633a 100%)', border: '1px solid #b6dcff', borderRadius: 10, color: '#fff', display: 'flex', flexDirection: 'column', gap: 6, fontWeight: 'bold', justifyContent: 'center', marginBottom: 20, textAlign: 'center', width: '100%' };
const campaignDetailsStyle = { backgroundColor: '#f8f9fa', border: '1px solid #ddd', borderRadius: 10, marginBottom: 20, padding: 16 };
const creatorTypePillStyle = { backgroundColor: '#eef7ff', border: '1px solid #b6dcff', borderRadius: 999, color: '#204766', display: 'inline-block', fontSize: 13, fontWeight: 'bold', marginBottom: 10, padding: '6px 12px' };
const messageStyle = { color: '#444', lineHeight: 1.5, margin: '10px 0 0', whiteSpace: 'pre-wrap' };
const stepGridStyle = { display: 'grid', gap: 12, gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', marginBottom: 22 };
const stepCardStyle = (complete) => ({ backgroundColor: complete ? '#effaf1' : '#f8f9fa', border: `1px solid ${complete ? '#28a745' : '#d8dee6'}`, borderRadius: 8, color: complete ? '#155724' : '#555', display: 'flex', flexDirection: 'column', gap: 6, padding: 14 });
const timerStyle = { backgroundColor: '#f8f9fa', border: '1px solid #ddd', borderRadius: 10, marginBottom: 20, padding: 16 };
const timerHeaderStyle = { display: 'flex', flexWrap: 'wrap', gap: 16, justifyContent: 'space-between', marginBottom: 10 };
const progressTrackStyle = { backgroundColor: '#e1e7ef', borderRadius: 999, height: 10, overflow: 'hidden' };
const progressFillStyle = { height: '100%', transition: 'width 0.3s ease' };
const helperStyle = { color: '#666', display: 'block', fontSize: 12, marginTop: 8 };
const feedbackFieldsetStyle = { border: '1px solid #ddd', borderRadius: 10, margin: '0 0 20px', padding: 16 };
const feedbackLegendStyle = { color: '#333', fontWeight: 'bold', padding: '0 6px' };
const feedbackOptionsStyle = { display: 'flex', flexWrap: 'wrap', gap: 10, marginTop: 8 };
const feedbackOptionStyle = (selected) => ({ backgroundColor: selected ? '#f8fbff' : '#fff', border: selected ? '2px solid #0f766e' : '1px solid #ccc', borderRadius: 8, cursor: 'pointer', minWidth: 90, padding: '10px 14px', textAlign: 'center' });
const labelStyle = { color: '#333', display: 'block', fontWeight: 'bold', marginBottom: 8 };
const textareaStyle = { border: '1px solid #ccc', borderRadius: 8, boxSizing: 'border-box', fontFamily: 'Arial, sans-serif', fontSize: 14, padding: 10, resize: 'vertical', width: '100%' };
const alreadySubmittedStyle = { backgroundColor: '#eef7ff', border: '1px solid #b6dcff', borderRadius: 10, color: '#204766', fontWeight: 'bold', margin: '16px 0', padding: 14, textAlign: 'center' };
const legacyLearnMoreStyle = { marginBottom: 20, marginTop: 16 };
const learnMoreButtonStyle = { backgroundColor: '#fff', border: '1px solid #bfdbfe', borderRadius: 8, color: '#2563eb', cursor: 'pointer', fontSize: 15, fontWeight: 'bold', padding: '12px 20px', width: '100%' };
const rewardNoteStyle = { backgroundColor: '#fff8e1', border: '1px solid #ffe08a', borderRadius: 10, color: '#6b5300', fontWeight: 'bold', margin: '20px 0', padding: 15, textAlign: 'center' };
const trustedStatusStyle = (status) => {
  const isProblem = ['error', 'insufficient_funding', 'already_completed'].includes(status);
  const isSuccess = status === 'settled';
  return {
    backgroundColor: isSuccess ? '#d4edda' : isProblem ? '#fff3f3' : '#eef7ff',
    border: `1px solid ${isSuccess ? '#28a745' : isProblem ? '#f1aeb5' : '#b6dcff'}`,
    borderRadius: 10,
    color: isSuccess ? '#155724' : isProblem ? '#842029' : '#204766',
    fontWeight: 'bold',
    margin: '16px 0',
    padding: 14,
    textAlign: 'center'
  };
};
const completeButtonStyle = (enabled) => ({ backgroundColor: enabled ? '#28a745' : '#b8c2cc', border: 'none', borderRadius: 8, color: '#fff', cursor: enabled ? 'pointer' : 'not-allowed', fontSize: 16, fontWeight: 'bold', padding: '14px 24px', width: '100%' });
const nextButtonStyle = (disabled) => ({ backgroundColor: '#ffffff', border: '1px solid #bfdbfe', borderRadius: 8, color: disabled ? '#8aa4c2' : '#2563eb', cursor: disabled ? 'not-allowed' : 'pointer', fontSize: 16, fontWeight: 'bold', marginTop: 12, padding: '14px 24px', width: '100%' });
const secondaryButtonStyle = { backgroundColor: '#007bff', border: 'none', borderRadius: 8, color: '#fff', cursor: 'pointer', fontSize: 16, padding: '12px 24px' };
const noticeStyle = (type) => ({ backgroundColor: type === 'success' ? '#d4edda' : '#cce7ff', border: `1px solid ${type === 'success' ? '#28a745' : '#007bff'}`, borderRadius: 8, boxShadow: '0 4px 12px rgba(0,0,0,0.1)', color: type === 'success' ? '#155724' : '#004085', fontSize: 14, fontWeight: 'bold', maxWidth: 320, padding: '15px 20px', position: 'fixed', right: 20, top: 20, zIndex: 1000 });
const destinationPreviewStyle = { backgroundColor: '#f8fbff', border: '1px solid #d8dee6', borderRadius: 10, marginBottom: 20, overflow: 'hidden', padding: 14 };
const destinationHeaderStyle = { alignItems: 'center', color: '#25364a', display: 'flex', flexWrap: 'wrap', gap: 10, justifyContent: 'space-between', marginBottom: 12 };
const destinationProviderPillStyle = { backgroundColor: '#e8f5ee', border: '1px solid #b7e3ca', borderRadius: 999, color: '#17633a', fontSize: 12, fontWeight: 'bold', padding: '5px 10px' };
const destinationEmbedStyle = { border: '1px solid #ddd', borderRadius: 10, minHeight: 180, width: '100%' };
const destinationFallbackVisualStyle = { aspectRatio: '16 / 9', backgroundColor: '#132033', borderRadius: 10, overflow: 'hidden', position: 'relative', width: '100%' };
const destinationBrandFallbackStyle = { alignItems: 'center', color: '#fff', display: 'flex', flexDirection: 'column', height: '100%', justifyContent: 'center', letterSpacing: 0, textAlign: 'center' };
const destinationOverlayStyle = { background: 'linear-gradient(180deg, rgba(0,0,0,0), rgba(0,0,0,0.72))', bottom: 0, color: '#fff', display: 'flex', flexDirection: 'column', gap: 4, left: 0, padding: 14, position: 'absolute', right: 0 };
const compactDestinationCopyStyle = { color: '#52616f', fontSize: 14, lineHeight: 1.45, margin: '0 0 12px' };
const destinationButtonStyle = { background: 'linear-gradient(45deg, #0f766e, #2563eb)', border: 'none', borderRadius: 8, color: '#fff', cursor: 'pointer', fontSize: 16, fontWeight: 'bold', marginTop: 12, padding: '14px 24px', width: '100%' };
const grindRowStyle = { alignItems: 'center', display: 'flex', flexWrap: 'wrap', gap: 10, margin: '-4px 0 20px' };
const grindButtonStyle = (disabled) => ({ backgroundColor: disabled ? '#f1f5f9' : '#fff7ed', border: `1px solid ${disabled ? '#cbd5e1' : '#fdba74'}`, borderRadius: 999, color: disabled ? '#64748b' : '#9a3412', cursor: disabled ? 'default' : 'pointer', fontSize: 15, fontWeight: 'bold', padding: '9px 14px' });
const grindHelpStyle = { color: '#666', fontSize: 13 };
const mediaSectionStyle = { marginBottom: 20 };
const sectionHeadingStyle = { color: '#333', fontSize: 16, margin: '0 0 10px' };
const mediaEmbedStyle = { border: '1px solid #ddd', borderRadius: 10, minHeight: 220, width: '100%' };
const trustedMediaLinkStyle = { backgroundColor: '#f8f9fa', border: '1px solid #ddd', borderRadius: 10, color: '#007bff', display: 'block', fontWeight: 'bold', padding: 14, textAlign: 'center', textDecoration: 'none' };

export default UnifiedDiscoveryFeed;
